import {Vector3} from 'three';

import {
  checkExhaustive,
  checkExists,
  checkState,
} from 'external/dev_april_corgi+/js/common/asserts';
import {
  DefaultMap,
  getOnlyElement,
} from 'external/dev_april_corgi+/js/common/collections';

import {EMERALD_600_RGBA, RgbaU32} from '../types/colors';
import {
  DataType,
  DataValue,
  FloatValue,
  IntValue,
  MaterialId,
  MaterialValue,
  WellId,
} from '../types/data';
import {unitToNanos} from '../types/units';

import {fromJson, toJson} from './json';
// Cyclic deps #yolo
import {PipedGraphOverlay} from './pipes';

export type NodeId = string & {_brand: 'node'};
export type PipeId = {id: string; node: NodeId};

export type TypedDataValue<K extends DataValue['kind']> = DataValue & {
  kind: K;
};

export interface PlateConstraint {
  wells: Map<WellId, DataConstraints>;
}

export interface VolumeConstraint {
  numberNl: number;
}

export interface DataConstraints {
  array?: DataConstraints[];
  plate?: PlateConstraint;
  type?: DataType;
  value?: {
    expected: DataValue;
    reason: string;
  };
  volume?: VolumeConstraint;
}

export interface NodeSpec {
  id: string;
  label: string;
  inputs: PadSpec[];
  outputs: PadSpec[];
  pipe?(params: {node: Node; overlay: PipedGraphOverlay; graph: Graph}): void;
}

export interface PadSpec {
  id: string;
  label: string;
  type: Readonly<DataType>;
}

export interface Link {
  id: string;
  output: Pad<'output'>;
  input: Pad<'input'>;
}

export interface Node {
  id: NodeId;
  behavior:
    | {kind: 'js'; id: string}
    | {kind: 'subgraph'; graphId: string; subgraphId: string};
  color: RgbaU32;
  label: string;
  position: Vector3;
  subgraphId: string | undefined;
  inputs: Map<string, Pad<'input'>>;
  outputs: Map<string, Pad<'output'>>;
  pipe?(params: {node: Node; overlay: PipedGraphOverlay; graph: Graph}): void;
}

export interface Pad<T extends 'input' | 'output'> {
  id: string;
  node: Node;
  direction: T;
  label: string;
  type: Readonly<DataType>;
  // Maps from monotonic link ID to Link
  links: Map<string, Link>;
  unlinked?: T extends 'input'
    ? {
        constraints: DataConstraints;
        value: Readonly<DataValue>;
      }
    : never;
}

interface AddLink {
  kind: 'add_link';
  link: Link;
}

interface AddNode {
  kind: 'add_node';
  node: Node;
  position: Vector3;
}

interface DeleteLink {
  kind: 'delete_link';
  link: Link;
}

interface DeleteNode {
  kind: 'delete_node';
  node: Node;
}

interface MoveNode {
  kind: 'move_node';
  node: Node;
  position: Vector3;
}

interface UpdateNode {
  kind: 'update_node';
  node: Node;
}

export type Patch =
  | AddLink
  | AddNode
  | DeleteLink
  | DeleteNode
  | MoveNode
  | UpdateNode;

export interface Listener {
  patch(patches: Patch[]): Promise<void>;
}

interface SerializedGraph {
  lastId: number;
  nodes: Array<{
    id: NodeId;
    behavior:
      | {kind: 'js'; id: string}
      | {kind: 'subgraph'; graphId: string; subgraphId: string};
    color: RgbaU32;
    label: string;
    position: [x: number, y: number, z: number];
    subgraphId: string | undefined;
    inputs: Array<{
      id: string;
      direction: 'input';
      label: string;
      type: DataType;
      unlinked:
        | {
            constraints: DataConstraints;
            value: DataValue;
          }
        | undefined;
    }>;
    outputs: Array<{
      id: string;
      direction: 'output';
      label: string;
      type: DataType;
    }>;
  }>;
  links: Array<{
    id: string;
    output: {
      node: NodeId;
      pad: string;
    };
    input: {
      node: NodeId;
      pad: string;
    };
  }>;
}

const CURRENT_VERSION = 7;
const SERIALIZATION_ENCODING = 'gzip';

export class Graph {
  private readonly behaviors: Map<string, NodeSpec>;
  private readonly listeners: Listener[];
  readonly nodes: Map<NodeId, Node>;
  private lastId: number;
  private name: string;
  // TODO(april): move this out of here, move behaviors out of here too, and move this into the
  // `types` folder
  overlay: PipedGraphOverlay | undefined;

  constructor() {
    this.behaviors = new Map();
    this.listeners = [];
    this.nodes = new Map();
    this.lastId = 0;
    this.name = '(unknown)';
  }

  addBehaviors(specs: NodeSpec[]): void {
    for (const spec of specs) {
      this.behaviors.set(spec.id, spec);
    }
  }

  getName(): string {
    return this.name;
  }

  setName(name: string): void {
    this.name = name;
  }

  async addLink(output: Pad<'output'>, input: Pad<'input'>): Promise<void> {
    // Ensure we don't add duplicate links
    for (const link of output.links.values()) {
      if (link.input === input) {
        return;
      }
    }

    const link = {id: this.nextId(), output, input, constraints: {}};
    output.links.set(link.id, link);
    input.links.set(link.id, link);

    await this.patch([{kind: 'add_link', link}]);
  }

  addListener(listener: Listener): void {
    this.listeners.push(listener);
  }

  async addNode(position: Vector3, spec: NodeSpec): Promise<Node> {
    // TODO(april): should the position really go here? Feels shady since position is presentation.
    // Also it's kind of funny to take it as Vector3 since z always should equal 0 but why not...
    const node: Node = {
      id: this.nextId() as NodeId,
      label: spec.label,
      behavior: {kind: 'js', id: spec.id},
      color: EMERALD_600_RGBA,
      position,
      subgraphId: undefined,
      inputs: new Map(),
      outputs: new Map(),
      pipe: spec.pipe,
    };

    for (const pad of spec.inputs) {
      node.inputs.set(pad.id, {
        id: pad.id,
        label: pad.label,
        type: pad.type,
        node,
        direction: 'input',
        links: new Map(),
        unlinked: undefined,
      });
    }
    for (const pad of spec.outputs) {
      node.outputs.set(pad.id, {
        id: pad.id,
        label: pad.label,
        type: pad.type,
        node,
        direction: 'output',
        links: new Map(),
      });
    }

    this.nodes.set(node.id, node);
    await this.patch([
      {
        kind: 'add_node',
        node,
        position,
      },
    ]);
    return node;
  }

  async addSubgraph(
    position: Vector3,
    id: string,
    subgraph: Graph,
  ): Promise<void> {
    const localSubgraphId = this.nextId() as NodeId;
    const links = new Set<Link>();
    const remappedSubgraphs = new DefaultMap<string, string>(() =>
      this.nextId(),
    );
    // Push all nodes into the subgraph, but leave nodes already in a subgraph inside their
    // hierarchy so we don't have issues. We also have to remap subgraph IDs because they may
    // conflict with the existing graph.
    for (const node of subgraph.nodes.values()) {
      const newId = this.nextId() as NodeId;
      // If the node has any subgraph inputs, they will not match once the node and subgraph ID is
      // updated. So fix them now before we do anything else.
      if (node.subgraphId) {
        const remappedSubgraphId = remappedSubgraphs.get(node.subgraphId);
        const inputPad = node.inputs.get(
          subgraphIoPadId(node.subgraphId, node.id, 'input'),
        );
        if (inputPad) {
          node.inputs = rekeyPreservingOrder(
            node.inputs,
            inputPad,
            subgraphIoPadId(remappedSubgraphId, newId, 'input'),
            inputPad.label,
          );
        }
        const outputPad = node.outputs.get(
          subgraphIoPadId(node.subgraphId, node.id, 'output'),
        );
        if (outputPad) {
          node.outputs = rekeyPreservingOrder(
            node.outputs,
            outputPad,
            subgraphIoPadId(remappedSubgraphId, newId, 'output'),
            outputPad.label,
          );
        }
      }

      node.id = newId;
      this.nodes.set(node.id, node);
      for (const output of node.outputs.values()) {
        for (const link of output.links.values()) {
          links.add(link);
        }
      }

      // Remap the behavior's subgraph
      if (node.behavior.kind === 'subgraph') {
        node.behavior.subgraphId = remappedSubgraphs.get(
          node.behavior.subgraphId,
        );
      }

      const topLevel = node.subgraphId === undefined;
      node.subgraphId =
        node.subgraphId === undefined
          ? localSubgraphId
          : remappedSubgraphs.get(node.subgraphId);
      if (
        topLevel &&
        node.behavior.kind === 'js' &&
        node.behavior.id === 'node_subgraph_input'
      ) {
        const id = subgraphIoPadId(localSubgraphId, node.id, 'input');
        node.inputs.set(id, {
          id,
          node,
          direction: 'input',
          label: node.label,
          type: getOnlyElement(node.outputs.values()).type,
          links: new Map(),
          unlinked: undefined,
        });
      } else if (
        topLevel &&
        node.behavior.kind === 'js' &&
        node.behavior.id === 'node_subgraph_output'
      ) {
        const id = subgraphIoPadId(localSubgraphId, node.id, 'output');
        node.outputs.set(id, {
          id,
          node,
          direction: 'output',
          label: node.label,
          type: getOnlyElement(node.inputs.values()).type,
          links: new Map(),
          unlinked: undefined,
        });
      }
    }

    // Remap the link IDs too
    for (const link of links) {
      link.id = this.nextId();
    }

    const node: Node = {
      id: this.nextId() as NodeId,
      label: subgraph.name,
      behavior: {kind: 'subgraph', graphId: id, subgraphId: localSubgraphId},
      color: EMERALD_600_RGBA,
      position,
      subgraphId: undefined,
      inputs: new Map(),
      outputs: new Map(),
      pipe: undefined,
    };
    this.nodes.set(node.id, node);

    await this.patch([
      {
        kind: 'add_node',
        node,
        position,
      },
      ...[...subgraph.nodes.values()].map(
        n => ({kind: 'add_node', node: n, position: n.position}) as const,
      ),
    ]);
  }

  async deleteLink(link: Link): Promise<void> {
    link.output.links.delete(link.id);
    link.input.links.delete(link.id);
    await this.patch([
      {
        kind: 'delete_link',
        link,
      },
    ]);
  }

  async deleteNode(root: Node): Promise<void> {
    const nodes = [];
    const frontier = [root];
    while (frontier.length > 0) {
      const n = checkExists(frontier.pop());
      nodes.push(n);

      if (n.behavior.kind !== 'subgraph') {
        continue;
      }

      const subgraphId = n.behavior.subgraphId;
      for (const candidate of this.nodes.values()) {
        if (candidate.id !== n.id && candidate.subgraphId === subgraphId) {
          frontier.push(candidate);
        }
      }
    }

    const patches: Patch[] = [];
    const links = new Set<Link>();
    for (const node of nodes) {
      for (const pad of node.inputs.values()) {
        for (const [id, link] of pad.links) {
          link.output.links.delete(id);
          links.add(link);
        }
        pad.links.clear();
      }
      for (const pad of node.outputs.values()) {
        for (const [id, link] of pad.links) {
          link.input.links.delete(id);
          links.add(link);
        }
        pad.links.clear();
      }

      this.nodes.delete(node.id);
      patches.push({
        kind: 'delete_node',
        node,
      });
    }

    for (const link of links) {
      patches.push({
        kind: 'delete_link',
        link,
      });
    }
    await this.patch(patches);
  }

  async moveNode(node: Node, position: Vector3): Promise<void> {
    node.position = position;
    await this.patch([
      {
        kind: 'move_node',
        node,
        position,
      },
    ]);
  }

  async patchNode(node: Node): Promise<void> {
    await this.patch([
      {
        kind: 'update_node',
        node,
      },
    ]);
  }

  async clone(): Promise<Graph> {
    const bytes = await this.dump();
    const clone = new Graph();
    clone.addBehaviors([...this.behaviors.values()]);
    await clone.load(this.name, bytes, this.version());
    return clone;
  }

  async dump(): Promise<Uint8Array> {
    const string = toJson({
      lastId: this.lastId,
      nodes: [...this.nodes.values()].map(n => ({
        id: n.id,
        behavior: n.behavior,
        color: n.color,
        label: n.label,
        position: n.position.toArray(),
        subgraphId: n.subgraphId,
        inputs: [...n.inputs.values()].map(p => ({
          id: p.id,
          direction: p.direction,
          label: p.label,
          type: p.type,
          unlinked: p.unlinked,
        })),
        outputs: [...n.outputs.values()].map(p => ({
          id: p.id,
          direction: p.direction,
          label: p.label,
          type: p.type,
        })),
      })),
      links: [...this.nodes.values()].flatMap(n =>
        [...n.outputs.values()].flatMap(p =>
          [...p.links].map(([id, l]) => ({
            id,
            output: {
              node: l.output.node.id,
              pad: l.output.id,
            },
            input: {
              node: l.input.node.id,
              pad: l.input.id,
            },
          })),
        ),
      ),
    });
    const encoded = new TextEncoder().encode(string);
    const stream = new CompressionStream(SERIALIZATION_ENCODING);
    const writer = stream.writable.getWriter();
    writer.write(encoded);
    writer.close();
    return new Uint8Array(await new Response(stream.readable).arrayBuffer());
  }

  async load(name: string, bytes: Uint8Array, version: number) {
    this.name = name;

    const stream = new DecompressionStream(SERIALIZATION_ENCODING);
    const writer = stream.writable.getWriter();
    // Ensure this is a Uint8Array<ArrayBuffer>
    writer.write(new Uint8Array(bytes));
    writer.close();
    const json = await new Response(stream.readable)
      .arrayBuffer()
      .then(buffer => new TextDecoder().decode(buffer));
    const data: SerializedGraph = fromJson(json);
    checkState(this.nodes.size === 0);

    this.lastId = data.lastId;

    const patches: Patch[] = [];
    const roots = new Set<Node>();
    for (const node of data.nodes) {
      let behavior;
      let pipe;
      if (node.behavior.kind === 'js') {
        const b = checkExists(
          this.behaviors.get(migrateId(node.behavior.id, version)),
        );
        behavior = {
          kind: 'js',
          id: b.id,
        } as const;
        pipe = b.pipe;
      } else {
        behavior = node.behavior;
        pipe = undefined;
      }
      const deserialized: Node = {
        id: node.id as NodeId,
        behavior,
        color: node.color ?? EMERALD_600_RGBA,
        label: node.label,
        position: new Vector3().fromArray(node.position),
        subgraphId: node.subgraphId,
        inputs: new Map(),
        outputs: new Map(),
        pipe,
      };
      roots.add(deserialized);

      for (const p of node.inputs) {
        deserialized.inputs.set(p.id, {
          id: p.id,
          node: deserialized,
          direction: p.direction,
          label: p.label,
          type: p.type,
          links: new Map(),
          unlinked: p.unlinked,
        });
      }
      for (const p of node.outputs) {
        deserialized.outputs.set(p.id, {
          id: p.id,
          node: deserialized,
          direction: p.direction,
          label: p.label,
          type: p.type,
          links: new Map(),
          unlinked: undefined,
        });
      }
      this.nodes.set(node.id, deserialized);
      patches.push({
        kind: 'add_node',
        node: deserialized,
        position: deserialized.position,
      });
    }

    for (const serialized of data.links) {
      const output = checkExists(this.nodes.get(serialized.output.node));
      const input = checkExists(this.nodes.get(serialized.input.node));
      roots.delete(input);
      const link = {
        id: serialized.id,
        output: checkExists(output.outputs.get(serialized.output.pad)),
        input: checkExists(input.inputs.get(serialized.input.pad)),
        constraints: {},
      };
      link.output.links.set(link.id, link);
      link.input.links.set(link.id, link);
      patches.push({
        kind: 'add_link',
        link,
      });
    }

    migrate(this.nodes, version);
    await this.patch(patches);
  }

  async setValue(
    pad: Pad<'input'>,
    value: DataValue | undefined,
  ): Promise<void> {
    pad.unlinked = value ? {constraints: {}, value} : undefined;
    await this.patch([{kind: 'update_node', node: pad.node}]);
  }

  private async patch(patches: Patch[]): Promise<void> {
    for (const listener of this.listeners) {
      await listener.patch(patches);
    }
  }

  nextId(): string {
    return String(++this.lastId);
  }

  version(): number {
    return CURRENT_VERSION;
  }
}

export function arePadsCompatible(
  a: Pad<'input' | 'output'>,
  b: Pad<'input' | 'output'>,
): boolean {
  if (a.direction === b.direction) {
    return false;
  }

  const input = a.direction === 'input' ? a : b;
  const output = a.direction === 'output' ? a : b;

  if (output.type.kind === 'plate' && output.links.size > 0) {
    // Only allow plate outputs to be used once
    return false;
  }

  if (input.type.kind !== 'array') {
    if (input.links.size > 0) {
      return false;
    }
    if (output.type.kind === 'array' && input.type.kind !== 'any') {
      return false;
    }
  }

  if (output.type.kind === 'any' || input.type.kind === 'any') {
    return true;
  }

  return elementType(output.type).kind === elementType(input.type).kind;
}

export function isPadAndSpecCompatible(
  pad: Pad<'input' | 'output'>,
  other: PadSpec,
): boolean {
  const input = pad.direction === 'input' ? pad : other;
  const output = pad.direction === 'output' ? pad : other;

  if (input.type.kind !== 'array') {
    if (output.type.kind === 'array' && input.type.kind !== 'any') {
      return false;
    }
  }

  if (output.type.kind === 'any' || input.type.kind === 'any') {
    return true;
  }

  return elementType(output.type).kind === elementType(input.type).kind;
}

export function elementType(t: DataType): DataType {
  if (t.kind === 'array') {
    return elementType(t.elementsType);
  } else {
    return t;
  }
}

// Kinda weird to call this a hash but here we are
// Note that the ID is *not* part of the hash, so that we can set the ID to the hash
export function hashMaterial(m: MaterialValue): MaterialId {
  let amount;
  if (m.amount.kind === 'concentration') {
    const {number, unit} = unitToNanos(m.amount.number, m.amount.unit);
    amount = number + unit;
  } else if (m.amount.kind === 'volume') {
    amount = m.amount.numberNl + 'nl';
  } else {
    throw checkExhaustive(m.amount);
  }
  const id = toJson({
    label: m.label,
    amount,
    contents: m.contents
      .filter(c => {
        if (c.amount.kind === 'concentration') {
          return c.amount.number > 0;
        } else if (c.amount.kind === 'volume') {
          return c.amount.numberNl > 0;
        } else {
          throw checkExhaustive(c.amount);
        }
      })
      .map(c => fromJson(c.id)),
    tags: m.tags,
  });
  return id as unknown as MaterialId;
}

function migrate(nodes: Map<NodeId, Node>, version: number): void {
  if (version < 2) {
    for (const node of nodes.values()) {
      if (
        node.behavior.kind === 'js' &&
        node.behavior.id === 'node_magnetic_bead_wash'
      ) {
        node.inputs.set('input_volume', {
          id: 'input_volume',
          node,
          direction: 'input',
          label: 'Input volume',
          type: {
            kind: 'volume',
            minimumNl: 5_000,
            maximumNl: undefined,
          },
          unlinked: {
            constraints: {},
            value: {
              kind: 'volume',
              numberNl: 50_000,
              preferredUnit: 'ul',
            },
          },
          links: new Map(),
        });
      } else if (
        node.behavior.kind === 'js' &&
        node.behavior.id === 'node_mix'
      ) {
        for (const input of [...node.inputs.values()]) {
          if (input.id === 'water') {
            node.inputs = rekeyPreservingOrder(
              node.inputs,
              input,
              'remainder',
              'Remainder',
            );
          }
        }
      } else if (
        node.behavior.kind === 'js' &&
        node.behavior.id === 'node_mix_array'
      ) {
        for (const input of [...node.inputs.values()]) {
          if (input.id === 'premix') {
            node.inputs = rekeyPreservingOrder(
              node.inputs,
              input,
              'uniform',
              'Uniform',
            );
          } else if (input.id === 'water') {
            node.inputs = rekeyPreservingOrder(
              node.inputs,
              input,
              'remainder',
              'Remainder',
            );
          }
        }
      }
    }
  }
  if (version < 3) {
    for (const node of nodes.values()) {
      if (
        node.behavior.kind === 'js' &&
        node.behavior.id === 'node_serial_dilutions'
      ) {
        for (const input of [...node.inputs.values()]) {
          if (input.id === 'material') {
            node.inputs = rekeyPreservingOrder(
              node.inputs,
              input,
              'materials',
              'Materials',
            );
            input.type = {
              kind: 'array',
              elementsType: {kind: 'plated_material', material: undefined},
            };
          }
        }
      } else if (
        node.behavior.kind === 'js' &&
        node.behavior.id === 'node_mix_array'
      ) {
        for (const input of [...node.inputs.values()]) {
          if (input.id === 'premix') {
            node.inputs = rekeyPreservingOrder(
              node.inputs,
              input,
              'uniform',
              'Uniform',
            );
          } else if (input.id === 'water') {
            node.inputs = rekeyPreservingOrder(
              node.inputs,
              input,
              'remainder',
              'Remainder',
            );
          }
        }
      }
    }
  }
  if (version < 4) {
    for (const node of nodes.values()) {
      if (
        node.behavior.kind === 'js' &&
        node.behavior.id === 'node_magnetic_bead_wash'
      ) {
        node.inputs.set('wash_strength', {
          id: 'wash_strength',
          node,
          direction: 'input',
          label: 'Wash strength',
          type: {
            kind: 'float',
            minimum: 0,
            maximum: 1,
          },
          unlinked: undefined,
          links: new Map(),
        });
        node.inputs.set('wash_dilutant', {
          id: 'wash_dilutant',
          node,
          direction: 'input',
          label: 'Wash dilutant',
          type: {
            kind: 'plated_material',
            material: undefined,
          },
          unlinked: undefined,
          links: new Map(),
        });

        for (const input of [...node.inputs.values()]) {
          if (input.id === 'wash_solutions') {
            node.inputs = rekeyPreservingOrder(
              node.inputs,
              input,
              'wash_material',
              'Wash material',
            );
            input.type = {kind: 'plated_material', material: undefined};
            // Clear the existing default array value, who cares
            input.unlinked = undefined;
          }
        }
      }
    }
  }
  if (version < 5) {
    for (const node of nodes.values()) {
      if (
        node.behavior.kind === 'js' &&
        node.behavior.id === 'node_magnetic_bead_wash'
      ) {
        node.inputs.set('suffix', {
          id: 'suffix',
          node,
          direction: 'input',
          label: 'Suffix',
          type: {kind: 'string'},
          unlinked: undefined,
          links: new Map(),
        });
        node.outputs.set('supernatants', {
          id: 'supernatants',
          node,
          direction: 'output',
          label: 'Supernatants',
          type: {
            kind: 'plated_material',
            material: undefined,
          },
          unlinked: undefined,
          links: new Map(),
        });
        node.outputs.set('washed_outs', {
          id: 'washed_outs',
          node,
          direction: 'output',
          label: 'Washes',
          type: {
            kind: 'plated_material',
            material: undefined,
          },
          unlinked: undefined,
          links: new Map(),
        });
      }
    }
  }
  if (version < 6) {
    for (const node of nodes.values()) {
      if (
        node.behavior.kind === 'js' &&
        node.behavior.id === 'node_serial_dilutions'
      ) {
        const count = checkExists(node.inputs.get('count')?.unlinked)
          .value as IntValue;
        node.inputs.delete('count');
        const factor = checkExists(node.inputs.get('factor')?.unlinked)
          .value as FloatValue;
        node.inputs.delete('factor');
        const steps = count.number - 1;
        const to = Math.pow(factor.number, steps);
        node.inputs.set('spacing', {
          id: 'spacing',
          node,
          direction: 'input',
          label: 'Spacing',
          type: {
            kind: 'select',
            nullable: false,
            options: ['linear', 'logarithmic'],
          },
          unlinked: {
            constraints: {},
            value: {kind: 'select', value: 'logarithmic'},
          },
          links: new Map(),
        });
        node.inputs.set('from', {
          id: 'from',
          node,
          direction: 'input',
          label: 'From',
          type: {
            kind: 'float',
            minimum: 1,
            maximum: undefined,
          },
          unlinked: {
            constraints: {},
            value: {kind: 'float', number: 1, preferredDisplay: 'raw'},
          },
          links: new Map(),
        });
        node.inputs.set('to', {
          id: 'to',
          node,
          direction: 'input',
          label: 'To',
          type: {
            kind: 'float',
            minimum: 1,
            maximum: undefined,
          },
          unlinked: {
            constraints: {},
            value: {kind: 'float', number: to, preferredDisplay: 'raw'},
          },
          links: new Map(),
        });
        node.inputs.set('steps', {
          id: 'steps',
          node,
          direction: 'input',
          label: 'Steps',
          type: {
            kind: 'int',
            minimum: 1,
            maximum: undefined,
          },
          unlinked: {
            constraints: {},
            value: {kind: 'int', number: steps},
          },
          links: new Map(),
        });
      }
    }
  }
  if (version < 7) {
    for (const node of nodes.values()) {
      if (node.behavior.kind !== 'js') {
        continue;
      }
      if (node.behavior.id === 'node_material') {
        node.behavior.id = 'node_plated_material';
      } else if (node.behavior.id === 'node_output_material') {
        node.behavior.id = 'node_store_materials';
      }
    }
  }
}

export function padsForSubgraphNode(
  root: Node,
  graph: Graph,
): {
  outputs: Map<string, Pad<'output'>>;
  inputs: Map<string, Pad<'input'>>;
} {
  if (root.behavior.kind !== 'subgraph') {
    throw new Error('Expected subgraph node');
  }

  const inputs = new Map();
  const outputs = new Map();
  const subgraphId = root.behavior.subgraphId;
  for (const candidate of graph.nodes.values()) {
    if (candidate.subgraphId !== subgraphId) {
      continue;
    }

    for (const input of candidate.inputs.values()) {
      if (
        input.id === subgraphIoPadId(subgraphId, candidate.id, input.direction)
      ) {
        inputs.set(input.id, input);
      }
    }
    for (const output of candidate.outputs.values()) {
      if (
        output.id ===
        subgraphIoPadId(subgraphId, candidate.id, output.direction)
      ) {
        outputs.set(output.id, output);
      }
    }
  }
  return {inputs, outputs};
}

export function subgraphIoPadId(
  subgraphId: string,
  nodeId: NodeId,
  direction: 'input' | 'output',
): string {
  return `${subgraphId}/${nodeId}/${direction}`;
}

function migrateId(id: string, version: number): string {
  if (version < 7) {
    if (id === 'node_material') {
      return 'node_plated_material';
    } else if (id === 'node_output_material') {
      return 'node_store_materials';
    }
  }
  return id;
}

function rekeyPreservingOrder<T extends 'input' | 'output'>(
  map: Map<string, Pad<T>>,
  pad: Pad<T>,
  newId: string,
  newLabel: string,
): Map<string, Pad<T>> {
  const data = [...map];
  for (const item of data) {
    if (item[0] === pad.id) {
      item[0] = newId;
    }
  }
  pad.id = newId;
  pad.label = newLabel;
  return new Map(data);
}
